var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createReduxNucleusForm, nucleusFormShape } from '../NucleusForm';
import React from 'react';
import PropTypes from 'prop-types';
import Subscription from '../utils/Subscription';
import missingNucleusFormError from '../utils/missingNucleusFormError';
import shallowObjectEqual from '../utils/shallowObjectEqual';
import storeShape from '../utils/storeShape';
var withForm = function (getform) {
    /**
     * Connects to the form and allows parts of the form to be passed as props to the
     * wrapped component.
     * @param mapFormToProps - A function which takes the form and returns an object
     *        with the piece of form to be passed as props. A shallow comparison is
     *        performed on the returned object to determine if the wrapped component
     *        will be sent a new set of props.
     * @param mapFormActionsToProps - A function which takes the actions of a form
     *        and returns an object with the actions that will be returned as props.
     * @param mergeProps - A function that takes in the results of the mapFormToProps,
     *        mapFormActionsToProps, and any passed in props and merges them together.
     *        The default behavior is to merge them with mapFormToProps as the
     *        lowest priority and ownProps as the highest priority.
     */
    return function (mapFormToProps, mapFormActionsToProps, mergeProps) { return function (WrappedComponent) {
        var _a;
        return _a = /** @class */ (function (_super) {
                __extends(WithForm, _super);
                function WithForm(props, context) {
                    var _this = _super.call(this, props, context) || this;
                    _this.generateMappedForm = function () {
                        var form = _this.nucleusForm.selectors.getForm();
                        return mapFormToProps ? mapFormToProps(form) : undefined;
                    };
                    _this.handleFormUpdate = function () {
                        var mappedForm = _this.generateMappedForm();
                        if (!shallowObjectEqual(mappedForm, _this.state.mappedForm)) {
                            _this.setState({
                                mappedForm: mappedForm
                            });
                        }
                    };
                    _this.mergeProps = function () {
                        var mappedFormActions = mapFormActionsToProps ? mapFormActionsToProps(_this.nucleusForm.actions) : undefined;
                        if (mergeProps) {
                            return mergeProps(_this.state.mappedForm, mappedFormActions, _this.props);
                        }
                        return __assign(__assign(__assign({}, _this.state.mappedForm), mappedFormActions), _this.props);
                    };
                    var store = props.store || context.store;
                    var externalForm = _this.props.nucleusForm || context.nucleusForm;
                    _this.nucleusForm = getform(externalForm, store);
                    _this.state = {
                        mappedForm: _this.generateMappedForm()
                    };
                    _this.subscription = new Subscription(_this.nucleusForm.store, _this.handleFormUpdate);
                    _this.subscription.trySubscribe();
                    return _this;
                }
                WithForm.prototype.componentWillUnmount = function () {
                    this.subscription.tryUnsubscribe();
                };
                WithForm.prototype.render = function () {
                    return React.createElement(WrappedComponent, __assign({}, this.mergeProps()), this.props.children);
                };
                return WithForm;
            }(React.Component)),
            _a.displayName = 'WithForm',
            _a.contextTypes = {
                nucleusForm: nucleusFormShape,
                store: storeShape
            },
            _a.propTypes = {
                nucleusForm: nucleusFormShape,
                store: storeShape,
                children: PropTypes.node
            },
            _a;
    }; };
};
/**
 * Creates a withForm HOC component tied to a form within the redux state
 * @param formId - The id of the form the HOC is connected to.
 * @param formSelector - The selector that pulls the form from the overall state.
 */
export var withReduxForm = function (formId, formSelector) {
    var getForm = function (externalForm, store) {
        if (!store) {
            throw new Error('Could not find "store" in either the context or props of ' + '"WithForm". Either wrap the root component in a <Provider>, ' + 'or explicitly pass "store" as a prop to "WithForm".');
        }
        return createReduxNucleusForm(formId, store, formSelector);
    };
    return withForm(getForm);
};
/**
 * Creates a withForm HOC component that looks for the nucleusForm in context. this
 * component must exist within a form.
 * @param formId - The id of the form the HOC is connected to.
 * @param formSelector - The selector that pulls the form from the overall state.
 */
export default withForm(function (externalForm) {
    if (!externalForm) {
        throw missingNucleusFormError('WithForm');
    }
    return externalForm;
});
